import { useQuery, useMutation } from "@apollo/client/react";
import { Mail, School, Calendar as CalendarIcon } from "lucide-react";
import {
    Avatar,
    AvatarGradientFallback,
    Button,
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    Container,
    FieldError,
    Grid,
    Heading,
    Input,
    Label,
    PageSection,
    Skeleton,
    Spinner,
    StatusBadge,
} from "@athena/ui";
import { useState, useEffect } from "react";
import { formatDateTime } from "../../../global/formatHelpers";
import { ErrorFallback } from "@/components/ErrorFallback";
import { toast } from "sonner";
import { getInitialsFrom } from "../../../global/formatHelpers";
import { CurrentUserDocument } from "@/graphql/auth.generated";
import { UpdateUserDocument } from "@/graphql/users.generated";
import { formatPhoneInput } from "@/lib/phone";
import { useTranslation } from "react-i18next";
import { roleLabelMap, roleVariantMap } from "@/types/Roles";

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
}

export function ProfilePage() {
    const { t } = useTranslation();
    const { data, loading, error } = useQuery(CurrentUserDocument);
    const user = data?.currentUser;

    const [
        updateUser,
        { loading: saving },
    ] = useMutation(UpdateUserDocument);
    const [
        isEditing,
        setIsEditing,
    ] = useState<boolean>(false);

    const [
        formData,
        setFormData,
    ] = useState<FormData>({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
    });

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName ?? "",
                lastName: user.lastName ?? "",
                email: user.email ?? "",
                phoneNumber: user.phoneNumber ? formatPhoneInput(user.phoneNumber.replace(/^\+1/, "")) : "",
            });
        }
    }, [user]);

    const handleChange = (name: keyof FormData, value: string) => setFormData((prev) => ({ ...prev, [name]: value }));
    const phoneDigits = formData.phoneNumber.replace(/\D/g, "");
    const phoneInvalid = phoneDigits.length > 0 && phoneDigits.length !== 10;

    const handleSave = async () => {
        if (!user || phoneInvalid) return;
        try {
            await updateUser({
                variables: {
                    id: user.id,
                    input: {
                        firstName: formData.firstName.trim() || null,
                        lastName: formData.lastName.trim() || null,
                        // null (not undefined) so clearing the field actually clears it server-side —
                        // undefined means "field omitted", which the resolver treats as "leave as-is".
                        phoneNumber: formData.phoneNumber || null,
                    },
                },
            });
            setIsEditing(false);
            toast.success("Profile updated");
        } catch {
            toast.error("Failed to save. Please try again.");
        }
    };

    if (error) {
        return (
            <Container maxWidth="full" className="p-6">
                <ErrorFallback error={error} />
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="full" className="p-6">
                <Skeleton className="mb-6 h-9 w-full" />
                <Card className="max-w-full">
                    <CardContent className="p-6">
                        <div className="flex flex-col items-start gap-6 md:flex-row md:items-center">
                            <Skeleton className="h-20 w-20 shrink-0 rounded-full" />
                            <div className="flex-1 space-y-3">
                                <Skeleton className="h-7 w-48" />
                                <Skeleton className="h-4 w-64" />
                            </div>
                            <Skeleton className="h-10 w-20" />
                        </div>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    if (!user) return null;

    return (
        <Container maxWidth="full" className="p-6">
            <PageSection title="Profile" description="This page is all about you">
                <Grid cols={1}>
                    <Card className="max-w-full">
                        <CardContent className="p-6">
                            <div className="flex flex-col items-start gap-6 md:flex-row md:items-center">
                                <Avatar size="xl">
                                    <AvatarGradientFallback>
                                        {getInitialsFrom(
                                            user?.firstName ?? "",
                                            user?.lastName ?? "",
                                            user?.email ?? "",
                                        )}
                                    </AvatarGradientFallback>
                                </Avatar>
                                <div className="flex-1 space-y-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Heading level={3}>
                                            {user.firstName ?? user.email[0]} {user.lastName ?? ""}
                                        </Heading>
                                        {user.role && (
                                            <StatusBadge variant={roleVariantMap[user.role]} className="text-center">
                                                {roleLabelMap[user.role]}
                                            </StatusBadge>
                                        )}
                                    </div>
                                    <div className="text-muted-foreground flex flex-wrap gap-4 text-sm">
                                        <div className="flex items-center gap-1">
                                            <Mail className="size-4" />
                                            {user.email}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <School className="size-4" />
                                            {user.memberships[0]?.organization.name}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <CalendarIcon className="size-4" />
                                            {t("common.fields.joined")} {formatDateTime(user.createdAt)}
                                        </div>
                                    </div>
                                </div>
                                {isEditing ? (
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            className="w-auto"
                                            onClick={() => setIsEditing(false)}
                                            disabled={saving}
                                        >
                                            {t("common.actions.cancel")}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            className="w-auto"
                                            disabled={saving || phoneInvalid}
                                            onClick={handleSave}
                                        >
                                            {saving ? (
                                                <>
                                                    <Spinner />
                                                    {t("common.actions.saving")}
                                                </>
                                            ) : (
                                                "Save"
                                            )}
                                        </Button>
                                    </div>
                                ) : (
                                    <Button variant="secondary" className="w-auto" onClick={() => setIsEditing(true)}>
                                        {t("common.actions.editVariable", { variable: "Profile" })}
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {isEditing && (
                        <Card className="max-w-full">
                            <CardHeader>
                                <CardTitle>{t("common.fields.profileInfo")}</CardTitle>
                                <CardDescription>{t("common.descriptions.profile")}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="firstName">{t("common.fields.firstName")}</Label>
                                        <Input
                                            id="firstName"
                                            value={formData.firstName}
                                            onChange={(e) => handleChange("firstName", e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="lastName">{t("common.fields.lastName")}</Label>
                                        <Input
                                            id="lastName"
                                            value={formData.lastName}
                                            onChange={(e) => handleChange("lastName", e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">{t("common.fields.email")}</Label>
                                        <Input id="email" type="email" value={formData.email} disabled={true} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phoneNumber">{t("common.fields.phone")}</Label>
                                        <Input
                                            id="phoneNumber"
                                            type="tel"
                                            maxLength={12}
                                            value={formData.phoneNumber}
                                            onChange={(e) =>
                                                handleChange("phoneNumber", formatPhoneInput(e.target.value))
                                            }
                                            placeholder="555-555-5555"
                                            aria-invalid={phoneInvalid}
                                        />
                                        <FieldError>{phoneInvalid && "Enter a 10-digit phone number"}</FieldError>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </PageSection>
        </Container>
    );
}
